import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { useContext, useState } from "react";
import InitContext from "../pages/InitContext";
import Select from "react-select";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import tests from "../../resources/json/lab_test_types.json";
import {Consts} from "../pages/consts";

const LabValues = ({
  selectedPatient,
  onSaveNewLabTest,
  showDialog,
  hcpView,
}) => {
  const { t } = useTranslation();
  const [paramValues, setParamValues] = useState([]);
  const [drugHours, setDrugHours] = useState([]);
  const [selectedTest, setSelectedTest] = useState({});
  const context = useContext(InitContext);
  const [startDate, setStartDate] = useState();

  const { loading, error, clearError, saveDbLabTest, getDbDrugs } =
    useMmcService();

  const plasmaUnits = [
    { value: "ng/ml", label: "ng/ml" },
    { value: "µg/ml", label: "µg/ml" },
    { value: "mg/ml", label: "mg/ml" },
    { value: "nmol/l", label: "nmol/l" },
    { value: "µmol/l", label: "µmol/l" }
  ];

  const handleSaveLabTest = () => {
    clearError();
    const params = [];

    paramValues?.forEach((paramValue, i) => {
      if (paramValue) {
        params?.push({
          parameterName: selectedTest?.value?.name === "plasma_concentration_test"
            ? selectedTest?.value?.params[i] + "&&" + drugHours[i]
            : selectedTest?.value?.params[i],
          value: paramValue,
          unit: selectedTest?.value?.units[i],
        });
      }
    });

    const labTestToSave = {
      labTestName: selectedTest?.label,
      testDate: startDate,
      labParams: params,
    };

    const patientId = !selectedPatient
      ? context?.patient?.id
      : selectedPatient?.id;

    saveDbLabTest(context?.token, labTestToSave, patientId).then(() =>
      onSaveNewLabTest(labTestToSave),
    );
  };

  const handleSelectLabTest = async (test) => {
    if (test?.value?.key === Consts.PLASMA) {
      const patientId = !selectedPatient
        ? context?.patient?.id
        : selectedPatient?.id;

      const drugs = await getDbDrugs(context?.token, patientId, true, true);

      const myParams = drugs?.filter(drug => drug?.activeSubstance !== "")?.map((drug) => {
        return drug?.activeSubstance + "##" +  drug?.productName?.split(" ")[0];
      });

      const allParams = [];

      myParams.forEach((param) => {
        const mod = param.split("##");
        const substances = mod[0]?.split("&&&");
        const extendedSubst = substances.map((substance) => substance + "##" + t("drug") + ": " + mod[1]);
        allParams.push(...extendedSubst);
      })

      // ToDo here we need to add checking for similar hours for substances in the same drug
      const value = { ...test?.value, params: allParams };
      test = { ...test, value: value };
    }

    setSelectedTest(test);
  };

  const checkRequiredFields = () => {
    return startDate && selectedTest && paramValues?.length > 0;
  };

  const handleSelectingPlasmaUnit = (value, index) => {
    const unitsArr = selectedTest?.value?.units;
    unitsArr[index] = value;
    const myValue = { ...selectedTest?.value, units: unitsArr };
    const test = { ...selectedTest, value: myValue };
    setSelectedTest(test);
  };

  const handleSettingParamValue = (value, index) => {
    setParamValues((previousValues) => {
      let updatedValues = previousValues ? [...previousValues] : []; // initial array

      if(value){ // if value is not empty, set it
        updatedValues[index] = value;
      } else { // if value is empty, it means that the existing value on this place should be deleted
        if(previousValues) { // check if the array already has items
          updatedValues = previousValues?.filter((item, i) => i !== index); // delete the existing value
        }
      }

      return updatedValues;
    });
  };

  const handleHoursValue = (hours, index) => {
    setDrugHours((previousHours) => {
      let updatedHours = previousHours ? [...previousHours] : []; // initial array

      if(hours){ // if value is not empty, set it
        updatedHours[index] = hours;
      } else { // if value is empty, it means that the existing value on this place should be deleted
        if(previousHours) { // check if the array already has items
          updatedHours = previousHours?.filter((item, i) => i !== index); // delete the existing value
        }
      }

      return updatedHours;
    });
  };

  const errorMessage = error && (
    <ErrorMessage message={t("error_by_saving_lab_test_data")} />
  );

  const spinner = loading && <Spinner />;

  return (
    <div
      className="w3-modal"
      style={{ display: showDialog ? Consts.BLOCK : Consts.NONE }}
    >
      <div className="w3-modal-content w3-card-4 w3-animate-zoom w3-theme-l3 dialog-small">
        <Formik
          initialValues={{
            testDate: "",
            comment: "",
          }}
          onSubmit={handleSaveLabTest}
        >
          {({}) => (
            <Form>
              <header className="w3-container w3-center w3-padding-small">
                <h2>
                  {hcpView
                    ? t("please_enter_the_laboratory_test_data")
                    : t("please_enter_your_laboratory_test_data")}
                </h2>
              </header>
              {errorMessage}
              {spinner}
              <div className="w3-row w3-padding-small">
                <div className="w3-third">
                  <label>{t("laboratory_test")}</label>
                </div>
                <div className="w3-twothird">
                  <Select
                    id="labTestName"
                    options={tests}
                    onChange={(test) => handleSelectLabTest(test)}
                    formatOptionLabel={(test) => t(test?.label)}
                    placeholder={t("select")}
                    className="custom-select"
                    classNamePrefix="custom-select"
                  />
                </div>
              </div>
              <div className="w3-row w3-padding-small">
                <div className="w3-third ">
                  <label>{t("test_date")}:</label>
                </div>
                <div className="w3-twothird">
                  <input
                    id={Consts.DATE}
                    type={Consts.DATE}
                    className="w3-input w3-border w3-round form-input"
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </div>
              </div>
              { selectedTest?.value &&
                <div className="dialog-content-scroll w3-margin-top">
                  <table className="w3-table w3-bordered" style={{ width: '70%', tableLayout: 'auto', margin: '0 auto' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40%', padding: '0 10px' }}><label>{selectedTest?.value?.key === Consts.PLASMA ? t("medication") : t("parameter")}</label></th>
                        <th style={{ width: '20%', padding: '0 10px' }}><label>{ t("value") }</label></th>
                        <th style={{ width: '20%', padding: '0 10px' }}><label>{ t("unit") }</label></th>
                        {selectedTest?.value?.key === Consts.PLASMA && <th style={{ width: '15%', padding: '0 10px' }}><label>{ t("hours") }*</label></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTest?.value?.params?.map((param, i) => {
                        return (
                          <tr key={ `param${ selectedTest?.label }${ i }` }>
                            <td style={{ padding: '0 10px'}} >
                              <label>{ selectedTest?.value?.key === Consts.PLASMA ? param?.split("##")[0] + " (" + param?.split("##")[1] + ")" : t(param)}</label>
                            </td>
                            <td style={{ padding: '5px 10px'}}>
                              <input
                                id={ `param${ selectedTest?.label }${ i }` }
                                name={ Consts.VALUE }
                                type={ Consts.NUMBER }
                                min="0"
                                step="0.01"
                                className="form-input"
                                defaultValue={ paramValues ? paramValues[i] : "" }
                                onChange={ (event) =>
                                  handleSettingParamValue(event.target.value, i)
                                }
                              />
                            </td>
                            <td style={{ padding: '0 10px'}}>
                              { selectedTest?.value?.key === Consts.PLASMA ? (
                                <Select
                                  id={ Consts.UNIT }
                                  options={ plasmaUnits }
                                  onChange={ (unit) =>
                                    handleSelectingPlasmaUnit(unit.label, i)
                                  }
                                  placeholder={ t("select") }
                                  className="custom-select"
                                  classNamePrefix="custom-select"
                                />
                              ) : (
                                <label>
                                  { selectedTest?.value?.units[i] && selectedTest?.value?.units[i] }
                                </label>
                              ) }
                            </td>
                            <td style={{ padding: '0 10px'}}>
                              {selectedTest?.value?.key === Consts.PLASMA ? (
                                <input
                                  id={`paramHours${i}`}
                                  name={Consts.HOURS}
                                  type={Consts.NUMBER}
                                  min="1"
                                  className="form-input"
                                  defaultValue={drugHours ? drugHours[i] : ""}
                                  onChange={(event) =>
                                    handleHoursValue(event.target.value, i)
                                  }
                                />
                              ) : null }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              }
              <footer className="w3-container w3-center w3-padding-small">
                {selectedTest?.value?.key === Consts.PLASMA &&
                  <div>
                    <label style={{ paddingLeft: '20px', paddingTop: '20px' }}>* - {t("hours_since_intake")} </label>
                    <label style={{ paddingLeft: '20px', color: "red" } }>{t("equal_number_hours")} </label>
                  </div>
                }
                <div className="w3-right w3-padding-small">
                  <button
                    type="submit"
                    className="w3-btn w3-round-xlarge w3-theme-d5"
                    disabled={!checkRequiredFields()}
                  >
                    {t("submit")}
                  </button>
                  <button
                    onClick={() => onSaveNewLabTest(null)}
                    className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin"
                    type="button"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </footer>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LabValues;
